import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Check, Mail, Calendar, ExternalLink, AlertCircle, Plug, Instagram, Facebook, Music, RefreshCcw, Download } from "lucide-react";
import { toast } from "sonner";

const Integrations = () => {
  const [google, setGoogle] = useState(null);
  const [resend, setResend] = useState(null);
  const [social, setSocial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/integrations/google/status"),
      api.get("/integrations/resend/status"),
      api.get("/integrations/social/status"),
    ]).then(([g, r, s]) => {
      setGoogle(g.data);
      setResend(r.data);
      setSocial(s.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Show OAuth callback messages
    if (params.get("google_success")) {
      toast.success("Google Calendar conectado exitosamente");
      params.delete("google_success");
      setParams(params, { replace: true });
    }
    if (params.get("google_error")) {
      const err = params.get("google_error");
      const msg = err === "exchange_failed"
        ? "Error al intercambiar el código. Lo más común: el redirect_uri en tu Google Cloud Console no coincide. Copia el URI exacto que muestro abajo."
        : `Error al conectar Google: ${err}`;
      toast.error(msg, { duration: 8000 });
      params.delete("google_error");
      setParams(params, { replace: true });
    }
  }, []);

  const copyRedirectUri = () => {
    if (!google?.redirect_uri) return;
    navigator.clipboard.writeText(google.redirect_uri);
    toast.success("Redirect URI copiado");
  };

  const connectGoogle = async () => {
    try {
      const res = await api.get("/integrations/google/connect");
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("¿Desconectar Google? Las próximas reuniones no se sincronizarán.")) return;
    await api.post("/integrations/google/disconnect");
    toast.success("Desconectado");
    load();
  };

  const importEvents = async () => {
    setImporting(true);
    try {
      const res = await api.post("/integrations/google/import");
      toast.success(`${res.data.imported} eventos importados como reuniones`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setImporting(false); }
  };

  return (
    <Layout title="Integraciones" subtitle="Conecta tu stack externo">
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Google Calendar */}
          <div className="aura-card-flat p-6" data-testid="google-card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-[#F0F0F0] flex items-center justify-center">
                  <Calendar size={22} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-medium">Google Calendar</h3>
                  <div className="label-overline mt-1">Calendar + Meet + Sync bidireccional</div>
                </div>
              </div>
              <span className={`pill ${google?.connected ? "pill-green" : google?.configured ? "pill-yellow" : "pill-gray"} text-[10px]`}>
                {google?.connected ? "Conectado" : google?.configured ? "Listo para conectar" : "No configurado"}
              </span>
            </div>

            {google?.connected ? (
              <>
                <div className="aura-card-flat p-4 bg-[#F0F0F0] flex items-center gap-3 mb-4">
                  {google.google_picture && (
                    <img src={google.google_picture} alt={google.google_name} className="w-10 h-10 rounded-full" referrerPolicy="no-referrer" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{google.google_name || "Usuario Google"}</div>
                    <div className="text-xs text-[#666] truncate">{google.google_email}</div>
                  </div>
                  <Check size={16} className="text-green-600 flex-shrink-0" />
                </div>

                <div className="space-y-2 text-sm text-[#444] mb-4">
                  <div className="flex items-start gap-2"><Check size={14} className="text-green-600 mt-0.5" /> Cuando creas una reunión en Aura, se crea en tu Google Calendar</div>
                  <div className="flex items-start gap-2"><Check size={14} className="text-green-600 mt-0.5" /> Cuando alguien agenda por tu link público, se crea evento + Meet link automáticamente</div>
                  <div className="flex items-start gap-2"><Check size={14} className="text-green-600 mt-0.5" /> Cancelaciones se sincronizan a Calendar</div>
                  <div className="flex items-start gap-2"><Check size={14} className="text-green-600 mt-0.5" /> Puedes importar eventos existentes</div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <button onClick={importEvents} disabled={importing} className="btn-outline flex items-center gap-2" data-testid="import-events-btn">
                    {importing ? <span className="spinner" /> : <Download size={13} />}
                    Importar eventos
                  </button>
                  <button onClick={load} className="btn-ghost flex items-center gap-2">
                    <RefreshCcw size={13} /> Refrescar
                  </button>
                  <button onClick={disconnectGoogle} className="btn-ghost text-red-600 ml-auto">Desconectar</button>
                </div>
              </>
            ) : !google?.configured ? (
              <>
                <div className="bg-[#FFF4D6] border border-[#D99D26]/30 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={16} className="text-[#8a5e10] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[#444]">
                      <strong>Para activar Google Calendar:</strong> agrega GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET y GOOGLE_REDIRECT_URI en /app/backend/.env
                    </div>
                  </div>
                </div>
                <a href={google?.setup_instructions_url} target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center gap-2">
                  Ir a Google Cloud Console <ExternalLink size={13} />
                </a>
              </>
            ) : (
              <>
                <p className="text-sm text-[#666] mb-4">
                  Credenciales configuradas. Conecta tu cuenta de Google para sincronizar reuniones bidireccionalmente.
                </p>

                {/* Redirect URI panel */}
                <div className="aura-card-flat p-4 bg-[#FFF4D6] border border-[#D99D26]/40 mb-4">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertCircle size={14} className="text-[#8a5e10] flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-[#444]">
                      <strong>Si ves "redirect_uri_mismatch":</strong> el URI exacto que debes agregar en <a className="underline font-medium" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console → OAuth client → URIs de redireccionamiento autorizados</a> es:
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-white border border-[#D99D26]/40 rounded-lg p-2 font-mono text-xs break-all">
                    <span className="flex-1" data-testid="redirect-uri-value">{google?.redirect_uri || "(no configurado)"}</span>
                    <button onClick={copyRedirectUri} className="btn-ghost text-[11px] flex-shrink-0" data-testid="copy-redirect-uri">Copiar</button>
                  </div>
                  <ol className="list-decimal pl-5 mt-3 space-y-1 text-xs text-[#444]">
                    <li>Abre <a className="underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console → Credenciales</a></li>
                    <li>Click en tu OAuth 2.0 Client ID</li>
                    <li>En <strong>"URIs de redireccionamiento autorizados"</strong> pega el URI de arriba <strong>EXACTAMENTE</strong> (sin barra final extra)</li>
                    <li>Click Guardar. Espera ~30 segundos a que Google propague el cambio</li>
                    <li>Vuelve aquí y haz click en <strong>"Conectar Google"</strong></li>
                  </ol>
                </div>

                <button onClick={connectGoogle} className="btn-primary flex items-center gap-2" data-testid="connect-google">
                  <Plug size={14} /> Conectar Google
                </button>
                <div className="mt-5 border-t border-[#E5E5E5] pt-4">
                  <div className="label-overline mb-2">Permisos que se solicitarán</div>
                  <div className="flex flex-wrap gap-1">
                    {(google?.scopes || []).map((s) => (
                      <span key={s} className="pill pill-gray text-[10px] font-mono">{s.split("/").pop()}</span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Resend Email */}
          <div className="aura-card-flat p-6" data-testid="resend-card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-[#F0F0F0] flex items-center justify-center">
                  <Mail size={22} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-medium">Resend (Email)</h3>
                  <div className="label-overline mt-1">Confirmaciones de agendamiento</div>
                </div>
              </div>
              <span className={`pill ${resend?.configured ? "pill-green" : "pill-gray"} text-[10px]`}>
                {resend?.configured ? "Activo" : "No configurado"}
              </span>
            </div>
            {resend?.configured ? (
              <>
                <div className="space-y-2 text-sm text-[#444]">
                  <div className="flex items-start gap-2"><Check size={14} className="text-green-600 mt-0.5" /> Emails de confirmación al agendar reunión pública</div>
                  <div className="flex items-start gap-2"><Check size={14} className="text-green-600 mt-0.5" /> Notificación al host cuando alguien agenda</div>
                  <div className="flex items-start gap-2"><Check size={14} className="text-green-600 mt-0.5" /> Email de cancelación si se cancela</div>
                </div>
                <div className="mt-4 text-xs text-[#666]">
                  Enviando como: <code className="bg-[#F0F0F0] px-2 py-0.5 rounded font-mono">{resend.sender_email}</code>
                </div>
                <div className="mt-3 p-3 rounded-lg bg-[#FFF4D6] border border-[#D99D26]/30 text-xs text-[#444]">
                  Para enviar desde tu propio dominio (ej. <code>hola@rubiperfumeria.com</code>), verifica tu dominio en Resend → Domains y actualiza <code className="font-mono">SENDER_EMAIL</code> en el backend.
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-[#666] mb-4">
                  Sin RESEND_API_KEY las reservas se crean pero no se envían emails.
                </p>
                <ol className="list-decimal pl-5 space-y-1.5 text-sm text-[#444]">
                  <li>Crea cuenta en <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#8a5e10] font-medium hover:underline">resend.com</a></li>
                  <li>API Keys → Crear → copia el key (re_...)</li>
                  <li>Configúralo en backend/.env como RESEND_API_KEY</li>
                </ol>
              </>
            )}
          </div>

          {/* Social Platforms */}
          {social && Object.entries(social).map(([platform, status]) => {
            const Icon = platform === "instagram" ? Instagram : platform === "facebook" ? Facebook : platform === "tiktok" ? Music : ExternalLink;
            return (
              <div key={platform} className="aura-card-flat p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-[#F0F0F0] flex items-center justify-center">
                      <Icon size={20} className="text-[#0A0A0A]" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-medium capitalize">{platform}</h3>
                      <div className="label-overline mt-1">Publicación + Métricas</div>
                    </div>
                  </div>
                  <span className="pill pill-gray text-[10px]">Próximamente</span>
                </div>
                <p className="text-sm text-[#666]">{status.note}</p>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
};

export default Integrations;
